/**
 * PaintCanvas.ts — the paint-by-numbers board (Happy-Color-style).
 *
 * Layers (bottom→top): baseSprite (smooth photo) · fillLayer (white per-region "lids") ·
 * spreadLayer (transient touch-anchored paint floods) · inkLayer (persistent crisp vector
 * line-art that never fades) · overlayLayer (hint rings) · numbersLayer (region numbers).
 *
 * Feel:
 *  - DRAG to paint a continuous stroke (the signature Happy Color sweep) at fit zoom; drag pans
 *    when zoomed in. Single tap paints one region.
 *  - Fill is OPTIMISTIC + LOCAL: the paint floods from your finger the instant you touch
 *    (no network in the visual loop); the server reveal later carries the money FX.
 *  - The fill is a touch-anchored radial paint-spread in the cell's colour (clipped to the
 *    region) + lid recede, not a flat dissolve.
 *  - Hit-testing is point-in-polygon (what you touch is what fills), centroid only as fallback.
 *  - Pan has flick inertia; pinch + double-tap + wheel zoom; magnification adapts to region size.
 *
 * Vector strokes keep the line-art razor-sharp at any zoom and watertight by construction.
 */
import {
  Container, Graphics, Sprite, Assets, Text,
  type Texture, type FederatedPointerEvent, type FederatedWheelEvent,
} from 'pixi.js';
import theme, { hex, shade } from '../render/theme';
import { Tweens } from '../util/tween';
import { easeOutCubic, clamp } from '../util/ease';
import { makeText } from '../ui/widgets';
import type { CellDef } from '../net/contract';
import { Juice } from '../fx/Juice';
import { Audio } from '../util/audio';
import { reducedMotion } from '../util/motion';

const INK_COLOR = 0x241a33;     // near-black ink, a touch warm so it sits in the Calm-Premium palette
const INK_SCREEN_PX = 1.35;     // target on-screen line weight; redrawn on zoom to stay constant
const MAX_ZOOM = 6;             // how far the player can magnify (small regions need it)
const TAP_SLOP = 7;             // px of movement still treated as a tap (not a pan/stroke)
const PAN_FRICTION = 0.9;       // per-frame velocity decay for flick inertia
const ELASTIC = 0.4;            // resistance when dragging past the clamp limits

interface CellNode {
  def: CellDef;
  graphic: Graphics;  // white "lid" over unpainted regions; hidden on paint
  painted?: Graphics; // permanent flat palette-colour fill once painted
  numberLabel?: Text;
  filled: boolean;
  fillColor: number;
  lx: number; // number-anchor (board space)
  ly: number;
  indexInColor: number;
  inkMul: number; // per-region ink-weight multiplier (bolder outlines on bigger shapes)
}

export interface PaintCanvasCallbacks {
  /** the user tapped/swept a paintable cell of the active color. */
  onTapCell: (colorId: number, cellIndex: number, screenX: number, screenY: number) => void;
  /** a tap landed on nothing paintable (mis-tap / dead tap). */
  onMissTap?: (wrongColorId: number | null) => void;
  /** zoom level changed (1 = fit). Lets the scene toggle the zoom-to-fit affordance. */
  onZoom?: (zoom: number) => void;
}

export class PaintCanvas extends Container {
  private board = new Container();
  private baseSprite = new Sprite();      // (legacy) smooth posterized picture — covered now
  private fillLayer = new Container();     // white lids over unpainted regions
  private paintedLayer = new Container();  // permanent FLAT palette-colour fills (the painted state)
  private spreadLayer = new Container();   // transient touch-anchored paint floods
  private inkLayer = new Graphics();       // all region boundaries as persistent vector line-art
  private overlayLayer = new Container();  // transient hint rings + standing invite (above ink)
  private numbersLayer = new Container();

  private nodes: CellNode[] = [];
  private cellsByColor = new Map<number, CellNode[]>();
  private activeColorId: number | null = null;
  private paletteHex = new Map<number, string>();

  // transform: effective board scale = fitScale * zoom; board sits at (offsetX, offsetY)
  private fitScale = 1;
  private zoom = 1;
  private offsetX = 0;
  private offsetY = 0;
  private inited = false;
  private lastInkScale = -1;
  private get scale_(): number { return this.fitScale * this.zoom; }

  private view: number;
  private cb: PaintCanvasCallbacks;
  private inkLines: number[][] | null = null; // shared-edge network; null = stroke per-region (fallback)
  private boardW = 0;
  private boardH = 0;

  // gesture state
  private pointers = new Map<number, { x: number; y: number }>();
  private mode: 'idle' | 'paint' | 'pan' = 'idle';
  private panLast: { x: number; y: number } | null = null;
  private downAt: { x: number; y: number } | null = null;
  private moved = 0;
  private panVel = { x: 0, y: 0 };
  private strokePainted = new Set<number>(); // cellIds already filled this stroke (dedupe)
  private pinchBase: { dist: number; zoom: number } | null = null;
  private lastTapTime = -1;
  private lastTapPos = { x: 0, y: 0 };
  private prevZoom = 2.6; // remembered zoom for double-tap-out

  // standing "paint here next" invite
  private invite = new Graphics();
  private inviteNode: CellNode | null = null;
  private breath = 0;
  // ambient canvas breathing — a very subtle brightness rhythm on the artwork between events,
  // so the canvas is never bone-still (audit feel-review minor: "canvas is visually static")
  private canvasBreath = 0;

  constructor(
    cells: CellDef[],
    palette: { colorId: number; hex: string; number: number }[],
    view: number,
    cb: PaintCanvasCallbacks,
    imageUrl?: string,
    inkLines?: number[][],
  ) {
    super();
    this.view = view;
    this.cb = cb;
    this.inkLines = inkLines && inkLines.length ? inkLines : null;
    for (const p of palette) this.paletteHex.set(p.colorId, p.hex);

    this.baseSprite.width = view;
    this.baseSprite.height = view;
    this.board.addChild(
      this.baseSprite, this.fillLayer, this.paintedLayer, this.spreadLayer, this.inkLayer, this.overlayLayer, this.numbersLayer,
    );
    this.overlayLayer.addChild(this.invite);
    this.addChild(this.board);
    if (imageUrl) void this.loadBase(imageUrl);

    const colorCursor = new Map<number, number>();
    for (const def of cells) {
      const g = new Graphics();
      const idx = colorCursor.get(def.colorId) ?? 0;
      colorCursor.set(def.colorId, idx + 1);
      const node: CellNode = {
        def, graphic: g, filled: false,
        fillColor: hex(this.paletteHex.get(def.colorId) ?? '#cccccc'),
        lx: def.cx, ly: def.cy, indexInColor: idx, inkMul: 1,
      };
      this.nodes.push(node);
      if (!this.cellsByColor.has(def.colorId)) this.cellsByColor.set(def.colorId, []);
      this.cellsByColor.get(def.colorId)!.push(node);
      this.fillLayer.addChild(g);
    }
    // ink-weight tiers: bigger shapes get bolder outlines (a hand-drawn / coloring-book feel,
    // versus a uniform CAD line). Bucketed so drawInk stays a few stroke passes, not one-per-region.
    const radii = this.nodes.map((n) => n.def.labelR ?? 0).sort((a, b) => a - b);
    const q = (f: number) => radii[Math.min(radii.length - 1, Math.floor(radii.length * f))] ?? 0;
    const lo = q(0.5), hi = q(0.85);
    for (const n of this.nodes) {
      const r = n.def.labelR ?? 0;
      n.inkMul = r >= hi ? 1.3 : r >= lo ? 1.0 : 0.82;
    }

    this.eventMode = 'static';
    this.on('pointerdown', this.onDown, this);
    this.on('globalpointermove', this.onMove, this);
    this.on('pointerup', this.onUp, this);
    this.on('pointerupoutside', this.onUp, this);
    this.on('pointercancel', this.onUp, this);
    this.on('wheel', this.onWheel, this);

    this.drawStatic();
  }

  private async loadBase(url: string): Promise<void> {
    try {
      const tex = (await Assets.load(url)) as Texture;
      this.baseSprite.texture = tex;
      this.baseSprite.width = this.view;
      this.baseSprite.height = this.view;
    } catch {
      /* image missing -> lids simply reveal nothing; gameplay still works */
    }
  }

  /**
   * Build the canvas in two synchronous-cheap passes + one async (chunked) pass. The number-
   * label pass dominates cost because each region allocates a Pixi `Text` (with halo stroke);
   * doing all 100+ at once is the entry-hitch the audit flagged. We yield to the browser between
   * chunks via setTimeout(0).
   */
  private drawStatic(): void {
    this.numbersLayer.removeChildren();
    // pass 1 — lids (synchronous, cheap; needed before any tap registers)
    for (const node of this.nodes) {
      const poly = node.def.poly ?? [];
      if (poly.length < 6) continue;
      node.graphic.clear();
      node.graphic.poly(poly).fill({ color: 0xffffff, alpha: 1 });
      node.graphic.poly(poly).stroke({ width: 9, color: 0xffffff, alpha: 1 });
    }
    // pass 2 — vector ink (synchronous, one stroke call per weight tier; cheap)
    this.drawInk(true);
    // pass 3 — paint-numbers (async chunks, heaviest path)
    this.buildNumbersAsync();
  }

  /** Reports the number-build progress 0..1 so the scene can show a Loading…/skeleton overlay. */
  numbersProgress = 0;
  onNumbersReady: (() => void) | null = null;

  private buildNumbersAsync(): void {
    const CHUNK = 24;
    let i = 0;
    const step = () => {
      if (this.destroyed) return;
      const end = Math.min(i + CHUNK, this.nodes.length);
      for (; i < end; i++) {
        const node = this.nodes[i];
        const poly = node.def.poly ?? [];
        if (poly.length < 6) continue;
        const size = this.numberSize(node);
        // Each number is faintly tinted toward its target colour (darkened so the digit stays
        // legible on a white lid). This is one of those subtle parity details: a flat-ink number
        // grid looks engineered, while a row of softly-coloured numbers reads as "paint by
        // numbers" at a glance — closer to how the reference titles do it.
        const targetHex = this.paletteHex.get(node.def.colorId) ?? '#3a2c52';
        const tintedHex = shade(targetHex, -0.55); // darken so it's readable on white
        const t = makeText(String(node.def.colorId), { size, weight: '700', color: hex(tintedHex) });
        t.style.stroke = { color: 0xffffff, width: Math.max(1, size * 0.16), alpha: 0.55 };
        t.anchor.set(0.5);
        t.alpha = 0.78;
        t.position.set(node.lx, node.ly);
        node.numberLabel = t;
        this.numbersLayer.addChild(t);
      }
      this.numbersProgress = i / Math.max(1, this.nodes.length);
      if (i < this.nodes.length) {
        setTimeout(step, 0); // yield to the browser between chunks
      } else {
        this.numbersProgress = 1;
        this.onNumbersReady?.();
      }
    };
    step();
  }

  /** Font size (picture-space) that fits the paint-number inside the region's inscribed circle. */
  private numberSize(node: CellNode): number {
    const r = node.def.labelR ?? this.view / 28;
    const digits = String(node.def.colorId).length;
    const byHeight = r * 1.5;
    const byWidth = (r * 1.7) / Math.max(1, digits * 0.62);
    return Math.max(7, Math.min(34, Math.min(byHeight, byWidth)));
  }

  /** (Re)draw the ink line-art at a constant on-screen weight. */
  private drawInk(force = false): void {
    if (!force && Math.abs(this.scale_ - this.lastInkScale) < 1e-4) return; // pan doesn't change width
    this.lastInkScale = this.scale_;
    const w = INK_SCREEN_PX / (this.scale_ || 1);
    this.inkLayer.clear();

    if (this.inkLines) {
      // SHARED-EDGE network: each boundary is ONE open polyline → no doubled/parallel lines.
      // Drawn with moveTo/lineTo (open paths, not closed polygons) in a single stroke pass.
      for (const pl of this.inkLines) {
        if (pl.length < 4) continue;
        this.inkLayer.moveTo(pl[0], pl[1]);
        for (let i = 2; i + 1 < pl.length; i += 2) this.inkLayer.lineTo(pl[i], pl[i + 1]);
      }
      this.inkLayer.stroke({ width: w, color: INK_COLOR, alpha: 0.92, join: 'round', cap: 'round' });
      return;
    }

    // fallback (procedural pictures with no ink network): stroke region polygons in 3 weight tiers
    for (const mul of [0.82, 1.0, 1.3]) {
      let any = false;
      for (const node of this.nodes) {
        if (node.inkMul !== mul) continue;
        const poly = node.def.poly ?? [];
        if (poly.length < 6) continue;
        this.inkLayer.poly(poly);
        any = true;
      }
      if (any) this.inkLayer.stroke({ width: w * mul, color: INK_COLOR, alpha: 0.92, join: 'round', cap: 'round' });
    }
  }

  /** Fade the ink line-art away (used by the picture-complete reveal). */
  fadeInk(toAlpha = 0, durationMs = 600): void {
    Tweens.to({
      from: this.inkLayer.alpha, to: toAlpha, duration: durationMs, ease: easeOutCubic,
      onUpdate: (v) => { if (!this.inkLayer.destroyed) this.inkLayer.alpha = v; },
    });
  }

  /**
   * Peek mode: while held, fade every UNFILLED lid to a transparency that hints at the target
   * underneath without giving away the satisfaction of finishing. Release returns the lids to
   * fully opaque. Cheap and reversible — no allocations, just tween targets on `fillLayer.alpha`.
   */
  setPeek(on: boolean): void {
    const target = on ? 0.18 : 1;
    Tweens.to({
      from: this.fillLayer.alpha, to: target, duration: 220, ease: easeOutCubic,
      onUpdate: (v) => { if (!this.fillLayer.destroyed) this.fillLayer.alpha = v; },
    });
  }

  /** A quick auto-release peek flash for taps (vs press-and-hold). */
  flashPeek(): void {
    this.setPeek(true);
    setTimeout(() => this.setPeek(false), 800);
  }

  /** Set which color is currently paintable. */
  setActiveColor(colorId: number | null): void {
    this.activeColorId = colorId;
    for (const node of this.nodes) {
      if (node.filled || !node.numberLabel) continue;
      const active = node.def.colorId === colorId;
      const targetHex = this.paletteHex.get(node.def.colorId) ?? '#3a2c52';
      // active numbers pop in their bolder colour; inactive ones use a softer darker tint
      node.numberLabel.alpha = colorId == null ? 0.78 : active ? 1 : 0.35;
      node.numberLabel.style.fill = active
        ? hex(shade(targetHex, -0.35))
        : hex(shade(targetHex, -0.55));
    }
    this.updateInvite();
  }

  /** Move the standing breathing "paint here next" ring to the next unfilled active region. */
  private updateInvite(): void {
    this.inviteNode = null;
    if (this.activeColorId != null) {
      const nodes = this.cellsByColor.get(this.activeColorId);
      if (nodes) for (const n of nodes) { if (!n.filled) { this.inviteNode = n; break; } }
    }
    if (!this.inviteNode) this.invite.clear();
  }

  /** Get the next unfilled cell index (within color) for auto/guided tap. */
  nextUnfilledIndex(colorId: number): number | null {
    const nodes = this.cellsByColor.get(colorId);
    if (!nodes) return null;
    for (const n of nodes) if (!n.filled) return n.indexInColor;
    return null;
  }

  /** Screen position of a given cell (for FX anchoring), in this container's parent space. */
  cellScreenPos(colorId: number, indexInColor: number): { x: number; y: number } {
    const nodes = this.cellsByColor.get(colorId);
    const n = nodes?.[indexInColor];
    if (!n) return { x: this.boardW / 2, y: this.boardH / 2 };
    const gx = this.board.x + n.lx * this.scale_;
    const gy = this.board.y + n.ly * this.scale_;
    return { x: this.x + gx, y: this.y + gy };
  }

  /** A small gold sparkle on a cell (server told us this reveal was special). */
  flashSparkle(colorId: number, indexInColor: number): void {
    const n = this.cellsByColor.get(colorId)?.[indexInColor];
    if (n) Juice.glowPing(this.overlayLayer, n.lx, n.ly, hex(theme.palette.gold1), 22);
  }

  /** Flash every region of a just-completed colour together — "they all finish as one". */
  celebrateColor(colorId: number): void {
    const nodes = this.cellsByColor.get(colorId);
    if (!nodes) return;
    const g = new Graphics();
    this.overlayLayer.addChild(g);
    Tweens.to({
      from: 0, to: 1, duration: 620, ease: easeOutCubic,
      onUpdate: (t) => {
        if (g.destroyed) return;
        g.clear();
        const a = Math.sin(Math.min(1, t) * Math.PI);
        for (const n of nodes) {
          const poly = n.def.poly ?? [];
          if (poly.length >= 6) g.poly(poly).fill({ color: hex(theme.palette.gold1), alpha: 0.28 * a });
        }
      },
      onComplete: () => { if (!g.destroyed) g.destroy(); },
    });
  }

  /**
   * Commit the visual fill for a cell. Touch-anchored CLEAN radial spread (no ink-wobble — that
   * read chaotic in v2), tightened to ~190ms so the cell SNAPS to colour the way the reference
   * PBN games do, with a tiny scale-pop at landing. The number is faded out in the first 40%
   * of the duration so it's gone BEFORE the colour fully arrives instead of fighting it for
   * visibility halfway through.
   */
  fillCell(colorId: number, indexInColor: number, origin?: { x: number; y: number }): void {
    const node = this.cellsByColor.get(colorId)?.[indexInColor];
    if (!node || node.filled) return;
    node.filled = true;
    if (this.inviteNode === node) this.updateInvite();

    const poly = node.def.poly ?? [];
    const ox = origin?.x ?? node.lx;
    const oy = origin?.y ?? node.ly;
    const color = node.fillColor;

    // The PERMANENT painted state: a FLAT palette-colour polygon (the reference-PBN look — clean
    // flat colour between the black lines, NOT a revealed soft photo, which produced muddy
    // feathered edges). A wide same-colour seal-stroke makes the fill slightly overflow its
    // polygon so there are no white hairline cracks where neighbours' polygons don't perfectly
    // meet — the dark ink line on top hides the overlap.
    const paintFlat = () => {
      const g = node.painted ?? new Graphics();
      g.clear();
      g.poly(poly).fill({ color, alpha: 1 });
      g.poly(poly).stroke({ width: 6, color, alpha: 1 });
      if (!node.painted) { node.painted = g; this.paintedLayer.addChild(g); }
      if (!node.graphic.destroyed) node.graphic.alpha = 0; // hide the white lid underneath
      if (node.numberLabel) node.numberLabel.alpha = 0;
    };

    if (reducedMotion() || poly.length < 6) {
      paintFlat();
      return;
    }

    // Animate the flat colour FLOODING in from the touch point: a growing colour disc clipped to
    // the region. On completion we drop the permanent flat poly and bin the transient disc.
    let R = 1;
    for (let i = 0; i + 1 < poly.length; i += 2) {
      R = Math.max(R, Math.hypot(poly[i] - ox, poly[i + 1] - oy));
    }
    R *= 1.04;
    const mask = new Graphics().poly(poly).fill({ color: 0xffffff });
    const disc = new Graphics();
    disc.mask = mask;
    this.spreadLayer.addChild(mask, disc);

    const dur = 190;
    Tweens.to({
      from: 0, to: 1, duration: dur, ease: easeOutCubic,
      onUpdate: (t) => {
        if (disc.destroyed) return;
        disc.clear();
        disc.circle(ox, oy, R * t).fill({ color, alpha: 1 });
        const recede = 1 - Math.pow(1 - t, 2.2);
        if (!node.graphic.destroyed) node.graphic.alpha = 1 - recede;
        if (node.numberLabel) node.numberLabel.alpha = Math.max(0, 1 - t / 0.4) * 0.8;
      },
      onComplete: () => {
        paintFlat();               // lay down the permanent flat fill
        if (!disc.destroyed) disc.destroy();
        if (!mask.destroyed) mask.destroy();
      },
    });
  }

  // ---------------------------------------------------------------- gestures
  private onDown(e: FederatedPointerEvent): void {
    const p = this.toLocal({ x: e.global.x, y: e.global.y });
    this.pointers.set(e.pointerId, { x: p.x, y: p.y });
    this.panVel = { x: 0, y: 0 };
    if (this.pointers.size === 1) {
      this.panLast = { x: p.x, y: p.y };
      this.downAt = { x: p.x, y: p.y };
      this.moved = 0;
      this.strokePainted.clear();
      // at fit zoom a press starts painting immediately (sweep-to-fill); zoomed in it pans
      this.mode = this.zoom <= 1.001 ? 'paint' : 'pan';
      if (this.mode === 'paint' && this.activeColorId != null) Audio.beginSweep();
      if (this.mode === 'paint') this.paintAtGlobal(e.global.x, e.global.y);
    } else if (this.pointers.size === 2) {
      this.beginPinch();
      this.mode = 'pan';
      this.downAt = null;
    }
  }

  private onMove(e: FederatedPointerEvent): void {
    if (!this.pointers.has(e.pointerId)) return;
    const p = this.toLocal({ x: e.global.x, y: e.global.y });
    const prev = this.pointers.get(e.pointerId)!;
    this.pointers.set(e.pointerId, { x: p.x, y: p.y });

    if (this.pointers.size >= 2) { this.updatePinch(); return; }

    const dx = p.x - (this.panLast?.x ?? p.x);
    const dy = p.y - (this.panLast?.y ?? p.y);
    this.moved += Math.hypot(p.x - prev.x, p.y - prev.y);

    if (this.mode === 'paint') {
      // continuous sweep-to-fill: paint every active region the finger crosses
      this.paintAtGlobal(e.global.x, e.global.y);
    } else if (this.mode === 'pan' && this.zoom > 1.001) {
      this.panBy(dx, dy);
      this.panVel = { x: dx, y: dy };
    }
    this.panLast = { x: p.x, y: p.y };
  }

  private onUp(e: FederatedPointerEvent): void {
    const wasTap = this.pointers.size === 1 && this.downAt != null && this.moved <= TAP_SLOP;
    const tapPos = this.downAt ? { ...this.downAt } : null;
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinchBase = null;

    if (this.pointers.size === 0) {
      // a clean stationary press is a double-tap candidate (paint already happened on down)
      if (wasTap && tapPos) this.checkDoubleTap(tapPos.x, tapPos.y);
      this.panLast = null;
      this.downAt = null;
      this.mode = 'idle';
      if (this.zoom <= 1.001) this.panVel = { x: 0, y: 0 }; // no inertia at fit
      Audio.endSweep(); // close the sustained sweep body on release
    } else if (this.pointers.size === 1) {
      const remaining = this.pointers.values().next().value as { x: number; y: number };
      this.panLast = { ...remaining };
      this.mode = this.zoom <= 1.001 ? 'paint' : 'pan';
    }
  }

  private onWheel(e: FederatedWheelEvent): void {
    const p = this.toLocal({ x: e.global.x, y: e.global.y });
    const factor = Math.exp(-e.deltaY * 0.0015);
    const lx = (p.x - this.offsetX) / this.scale_;
    const ly = (p.y - this.offsetY) / this.scale_;
    this.zoomAboutLocal(lx, ly, this.zoom * factor, false, { sx: p.x, sy: p.y });
  }

  /** Double-tap: zoom toggle (paint on the taps still happened). */
  private checkDoubleTap(lx: number, ly: number): void {
    const now = performance.now();
    const isDouble = now - this.lastTapTime < 300 &&
      Math.hypot(lx - this.lastTapPos.x, ly - this.lastTapPos.y) < 28;
    this.lastTapTime = now; this.lastTapPos = { x: lx, y: ly };
    if (!isDouble) return;
    this.lastTapTime = -1;
    if (this.zoom > 1.001) { this.prevZoom = this.zoom; this.zoomToFit(); }
    else {
      const blx = (lx - this.offsetX) / this.scale_;
      const bly = (ly - this.offsetY) / this.scale_;
      this.zoomAboutLocal(blx, bly, Math.max(2, this.prevZoom), true, { sx: lx, sy: ly });
    }
  }

  /** Paint the region under a global point (point-in-polygon, centroid fallback). */
  private paintAtGlobal(gx: number, gy: number): void {
    if (this.activeColorId == null) return;
    const local = this.board.toLocal({ x: gx, y: gy });
    const nodes = this.cellsByColor.get(this.activeColorId);
    if (!nodes) return;

    // 1) exact containment among unfilled active regions
    let hit: CellNode | null = null;
    for (const n of nodes) {
      if (n.filled || this.strokePainted.has(n.def.cellId)) continue;
      if (pointInPoly(local.x, local.y, n.def.poly ?? [])) { hit = n; break; }
    }
    // 2) fallback: nearest centroid within a generous radius (helps tiny regions / fat fingers)
    if (!hit) {
      let bestD = Infinity;
      const maxR = (this.view / 18) * 2.4;
      for (const n of nodes) {
        if (n.filled || this.strokePainted.has(n.def.cellId)) continue;
        const d = (n.lx - local.x) ** 2 + (n.ly - local.y) ** 2;
        if (d < bestD) { bestD = d; hit = n; }
      }
      if (hit && Math.sqrt(bestD) > maxR) hit = null;
    }

    if (!hit) {
      // mis-tap: only react on a deliberate single tap, not while sweeping
      if (this.mode !== 'paint' || this.moved <= TAP_SLOP) this.missFeedback(local.x, local.y);
      return;
    }

    this.strokePainted.add(hit.def.cellId);
    // OPTIMISTIC local fill anchored at the finger — instant, no network in the visual loop
    this.fillCell(this.activeColorId, hit.indexInColor, { x: local.x, y: local.y });
    Juice.haptic(0.08); // soft tick per filled region (no-op where Vibration API is absent)
    const pos = this.cellScreenPos(this.activeColorId, hit.indexInColor);
    this.cb.onTapCell(this.activeColorId, hit.indexInColor, pos.x, pos.y);
  }

  /** Feedback when a tap hits nothing paintable: flash the wrong region + soft ripple. */
  private missFeedback(lx: number, ly: number): void {
    let wrong: CellNode | null = null;
    for (const n of this.nodes) {
      if (pointInPoly(lx, ly, n.def.poly ?? [])) { wrong = n; break; }
    }
    if (wrong && !wrong.filled) {
      // briefly flash the tapped region's number + a tick of its own colour so intent reads
      const g = new Graphics().poly(wrong.def.poly ?? []).stroke({ width: 3 / this.scale_, color: wrong.fillColor, alpha: 0.9 });
      this.overlayLayer.addChild(g);
      Tweens.to({
        from: 1, to: 0, duration: 420, ease: easeOutCubic,
        onUpdate: (a) => { if (!g.destroyed) g.alpha = a; },
        onComplete: () => { if (!g.destroyed) g.destroy(); },
      });
      if (wrong.numberLabel) Juice.squash(wrong.numberLabel, 0.25, 260);
    } else {
      Juice.glowPing(this.overlayLayer, lx, ly, theme.color.hint, 14);
    }
    this.cb.onMissTap?.(wrong ? wrong.def.colorId : null);
  }

  private panBy(dx: number, dy: number): void {
    const effW = this.view * this.scale_;
    const effH = this.view * this.scale_;
    // elastic resistance when already past the clamp edges
    const minX = Math.min(0, this.boardW - effW), maxX = 0;
    const minY = Math.min(0, this.boardH - effH), maxY = 0;
    const nx = this.offsetX + dx;
    const ny = this.offsetY + dy;
    this.offsetX = nx < minX ? minX + (nx - minX) * ELASTIC : nx > maxX ? maxX + (nx - maxX) * ELASTIC : nx;
    this.offsetY = ny < minY ? minY + (ny - minY) * ELASTIC : ny > maxY ? maxY + (ny - maxY) * ELASTIC : ny;
    this.board.position.set(this.offsetX, this.offsetY);
  }

  private beginPinch(): void {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return;
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    this.pinchBase = { dist, zoom: this.zoom };
    this.panLast = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
  }

  private updatePinch(): void {
    if (!this.pinchBase) { this.beginPinch(); return; }
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return;
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    const cx = (pts[0].x + pts[1].x) / 2;
    const cy = (pts[0].y + pts[1].y) / 2;
    // pan locked to the moving midpoint, zoom locked to the finger spread
    const lx = (cx - this.offsetX) / this.scale_;
    const ly = (cy - this.offsetY) / this.scale_;
    this.zoomAboutLocal(lx, ly, this.pinchBase.zoom * (dist / this.pinchBase.dist), false, { sx: cx, sy: cy });
    this.panLast = { x: cx, y: cy };
  }

  // ---------------------------------------------------------------- zoom / pan
  private zoomAboutLocal(
    lx: number, ly: number, targetZoom: number, animate: boolean, anchor?: { sx: number; sy: number },
  ): void {
    const z = clamp(targetZoom, 1, MAX_ZOOM);
    const sx = anchor?.sx ?? this.boardW / 2;
    const sy = anchor?.sy ?? this.boardH / 2;
    const eff = this.fitScale * z;
    const targetOX = sx - lx * eff;
    const targetOY = sy - ly * eff;
    if (!animate) {
      this.zoom = z; this.offsetX = targetOX; this.offsetY = targetOY;
      this.applyTransform();
      this.cb.onZoom?.(z);
      return;
    }
    const z0 = this.zoom, ox0 = this.offsetX, oy0 = this.offsetY;
    Tweens.to({
      from: 0, to: 1, duration: theme.motion.slow, ease: easeOutCubic,
      onUpdate: (t) => {
        this.zoom = z0 + (z - z0) * t;
        this.offsetX = ox0 + (targetOX - ox0) * t;
        this.offsetY = oy0 + (targetOY - oy0) * t;
        this.applyTransform();
      },
      onComplete: () => this.cb.onZoom?.(this.zoom),
    });
  }

  /** Reset to the centred fit view. */
  zoomToFit(): void {
    if (this.zoom > 1.001) this.prevZoom = this.zoom;
    const z0 = this.zoom, ox0 = this.offsetX, oy0 = this.offsetY;
    const targetOX = (this.boardW - this.view * this.fitScale) / 2;
    const targetOY = (this.boardH - this.view * this.fitScale) / 2;
    Tweens.to({
      from: 0, to: 1, duration: theme.motion.slow, ease: easeOutCubic,
      onUpdate: (t) => {
        this.zoom = z0 + (1 - z0) * t;
        this.offsetX = ox0 + (targetOX - ox0) * t;
        this.offsetY = oy0 + (targetOY - oy0) * t;
        this.applyTransform();
      },
    });
    this.cb.onZoom?.(1);
  }

  /**
   * If the next unfilled cell of `colorId` is OUT of the current viewport, fly the camera to it.
   * Mirrors Happy Color's "you can't see your remaining work, here it is" gesture. Called by the
   * scene after buy / color-complete so the player never has to hunt.
   */
  flyToNextActiveIfOffscreen(colorId: number): void {
    const next = this.nextUnfilledIndex(colorId);
    if (next == null) return;
    const node = this.cellsByColor.get(colorId)?.[next];
    if (!node) return;
    const sx = this.offsetX + node.lx * this.scale_;
    const sy = this.offsetY + node.ly * this.scale_;
    const margin = 60;
    const onScreen = sx >= margin && sy >= margin && sx <= this.boardW - margin && sy <= this.boardH - margin;
    if (onScreen) return;
    this.focusRegion(colorId, next);
  }

  /** Fly the camera to centre a region (zoom adapts to region size) and pulse a hint ring. */
  focusRegion(colorId: number, indexInColor: number): void {
    const node = this.cellsByColor.get(colorId)?.[indexInColor];
    if (!node) return;
    // smaller regions get magnified harder so the number/area is comfortably readable
    const r = node.def.labelR ?? 24;
    const targetR = 90; // desired on-screen-ish inscribed radius in fit units
    const z = clamp(this.fitScale > 0 ? targetR / Math.max(8, r) : 2.4, 1.6, 4.2);
    this.zoomAboutLocal(node.lx, node.ly, z, true);
    this.prevZoom = z;
    this.hintRing(node);
  }

  private hintRing(node: CellNode): void {
    const r = Math.max(18, (node.def.labelR ?? 24) * 1.5);
    const g = new Graphics();
    this.overlayLayer.addChild(g);
    Tweens.to({
      from: 0, to: 1, duration: 1100, ease: easeOutCubic,
      onUpdate: (t) => {
        if (g.destroyed) return;
        const rr = r * (0.6 + t * 0.9);
        g.clear();
        g.circle(node.lx, node.ly, rr).stroke({ width: 3 / this.scale_, color: hex(theme.palette.gold2), alpha: (1 - t) * 0.9 });
      },
      onComplete: () => { if (!g.destroyed) g.destroy(); },
    });
  }

  private applyTransform(): void {
    const effW = this.view * this.scale_;
    const effH = this.view * this.scale_;
    if (effW <= this.boardW) this.offsetX = (this.boardW - effW) / 2;
    else this.offsetX = Math.min(0, Math.max(this.boardW - effW, this.offsetX));
    if (effH <= this.boardH) this.offsetY = (this.boardH - effH) / 2;
    else this.offsetY = Math.min(0, Math.max(this.boardH - effH, this.offsetY));
    this.board.scale.set(this.scale_);
    this.board.position.set(this.offsetX, this.offsetY);
    this.drawInk();
  }

  /** Snap offsets back inside the hard clamp (after elastic overscroll / inertia). */
  private settleEdges(): void {
    const effW = this.view * this.scale_, effH = this.view * this.scale_;
    const minX = Math.min(0, this.boardW - effW), minY = Math.min(0, this.boardH - effH);
    const tx = effW <= this.boardW ? (this.boardW - effW) / 2 : clamp(this.offsetX, minX, 0);
    const ty = effH <= this.boardH ? (this.boardH - effH) / 2 : clamp(this.offsetY, minY, 0);
    if (Math.abs(tx - this.offsetX) > 0.5 || Math.abs(ty - this.offsetY) > 0.5) {
      const ox = this.offsetX, oy = this.offsetY;
      Tweens.to({
        from: 0, to: 1, duration: theme.motion.base, ease: easeOutCubic,
        onUpdate: (t) => { this.offsetX = ox + (tx - ox) * t; this.offsetY = oy + (ty - oy) * t; this.board.position.set(this.offsetX, this.offsetY); },
      });
    }
  }

  get zoomLevel(): number { return this.zoom; }

  /** Fit the board into w x h. Preserves zoom/pan across a re-layout (only resets on first fit). */
  fit(w: number, h: number): void {
    const reinit = !this.inited;
    this.boardW = w;
    this.boardH = h;
    this.fitScale = Math.min(w / this.view, h / this.view);
    if (reinit) {
      this.zoom = 1;
      this.offsetX = (w - this.view * this.fitScale) / 2;
      this.offsetY = (h - this.view * this.fitScale) / 2;
      this.inited = true;
    }
    this.applyTransform();
  }

  /** Per-frame: pan inertia glide + standing invite breathing. */
  tick(dtMs: number): void {
    // flick inertia (only when zoomed and not actively dragging)
    if (this.mode === 'idle' && this.zoom > 1.001 && (Math.abs(this.panVel.x) > 0.05 || Math.abs(this.panVel.y) > 0.05)) {
      this.panBy(this.panVel.x, this.panVel.y);
      const decay = Math.pow(PAN_FRICTION, dtMs / 16.67);
      this.panVel.x *= decay; this.panVel.y *= decay;
      if (Math.abs(this.panVel.x) < 0.05 && Math.abs(this.panVel.y) < 0.05) { this.panVel = { x: 0, y: 0 }; this.settleEdges(); }
    }
    // ambient canvas breath — gentle 4-second alpha pulse on the base image (only when the inkLayer
    // is fully visible — i.e. not during a picture-complete reveal where we fade it). Bounded to
    // 0.94..1 so it's felt as life, not a flicker.
    if (!reducedMotion() && this.inkLayer.alpha > 0.85) {
      this.canvasBreath += dtMs * 0.0008;
      this.baseSprite.alpha = 0.97 + Math.sin(this.canvasBreath) * 0.03;
    }

    // Standing "paint here next" highlights for ALL active-colour unfilled regions, with a
    // brighter pulse on the closest one. Replaces the earlier single-ring stale-pings bug.
    if (this.activeColorId != null) {
      this.breath += dtMs * 0.0045;
      const nodes = this.cellsByColor.get(this.activeColorId);
      if (nodes) {
        const lineW = 1.6 / this.scale_;
        this.invite.clear();
        const sin = Math.sin(this.breath);
        const a = 0.16 + sin * 0.06; // dim ambient float on every active cell
        for (const n of nodes) {
          if (n.filled) continue;
          const r = Math.max(10, (n.def.labelR ?? 14) * (1.12 + sin * 0.05));
          this.invite.circle(n.lx, n.ly, r).stroke({ width: lineW, color: hex(theme.palette.teal), alpha: a });
        }
        // brighter "paint this next" pulse on the first unfilled cell (the actual breathing ring)
        if (this.inviteNode) {
          const n = this.inviteNode;
          const base = Math.max(12, (n.def.labelR ?? 16) * 1.35);
          const pulse = 1 + sin * 0.12;
          this.invite.circle(n.lx, n.ly, base * pulse)
            .stroke({ width: 2.6 / this.scale_, color: hex(theme.palette.teal), alpha: 0.4 + sin * 0.14 });
        }
      }
    } else {
      this.invite.clear();
    }
  }
}

/** Ray-casting point-in-polygon for a flat [x0,y0,x1,y1,...] loop. */
function pointInPoly(x: number, y: number, poly: number[]): boolean {
  let inside = false;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2], yi = poly[i * 2 + 1];
    const xj = poly[j * 2], yj = poly[j * 2 + 1];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
